import React, { useState, useEffect } from 'react';
import {
  Icon,
  Segment,
  Form,
  TextArea,
  Header,
  Container,
  Grid,
  Message,
} from 'semantic-ui-react';
import { coursePageSaveScenario } from 'dsoapi';

const SavingIndicator = ({ saving, lastSaved }) => {
  return (
    <div
      style={{
        position: 'fixed',
        top: '20px',
        right: '20px',
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        color: 'white',
        padding: '8px 16px',
        borderRadius: '4px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        opacity: saving || lastSaved ? 1 : 0,
        transition: 'opacity 0.3s ease-in-out',
        zIndex: 1000,
      }}
    >
      {saving ? (
        <>
          <Icon name="circle notched" loading />
          Saving...
        </>
      ) : (
        <>
          <Icon name="check circle" color="green" />
          All changes saved
        </>
      )}
    </div>
  );
};

const CoursePageScenario = ({ courseData, setPageValid }) => {
  const [scenarios, setScenarios] = useState([]);
  const [debouncedScenarios, setDebouncedScenarios] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(false);

  useEffect(() => {
    if (courseData?.scenarioInfo?.scenarios) {
      setScenarios(
        courseData.scenarioInfo.scenarios.map((scenario) => ({
          ...scenario,
          studentAnswerText: scenario.studentAnswerText || '',
        }))
      );
    }
  }, [courseData]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedScenarios(scenarios);
    }, 1000);

    return () => {
      clearTimeout(timerId);
    };
  }, [scenarios]);

  useEffect(() => {
    if (debouncedScenarios) {
      const saveScenarios = async () => {
        setSaving(true);
        try {
          const saveRequest = {
            scenarios: debouncedScenarios.map((scenario) => ({
              userScenarioId: scenario.userScenarioId,
              studentAnswerText: scenario.studentAnswerText,
            })),
          };
          await coursePageSaveScenario(courseData.hashLink, saveRequest);
          setLastSaved(true);
          setTimeout(() => setLastSaved(false), 2000);
        } catch (error) {
          console.error('Error saving scenarios:', error);
        } finally {
          setSaving(false);
        }
      };
      saveScenarios();
    }
  }, [debouncedScenarios, courseData?.hashLink]);

  useEffect(() => {
    setPageValid(() => async () => {
      // Wait for any in-progress save to complete
      if (saving) {
        // Wait a short time for the save to complete
        await new Promise((resolve) => setTimeout(resolve, 100));
        // If still saving, wait a bit longer
        if (saving) {
          await new Promise((resolve) => setTimeout(resolve, 400));
        }
      }

      // Only do final save if scenarios have changed since last save
      if (
        scenarios.length > 0 &&
        JSON.stringify(scenarios) !== JSON.stringify(debouncedScenarios)
      ) {
        const saveRequest = {
          scenarios: scenarios.map((scenario) => ({
            userScenarioId: scenario.userScenarioId,
            studentAnswerText: scenario.studentAnswerText,
          })),
        };
        try {
          await coursePageSaveScenario(courseData.hashLink, saveRequest);
          return true;
        } catch (error) {
          console.error('Error saving scenarios:', error);
          return false;
        }
      }
      return true;
    });
  }, [
    setPageValid,
    scenarios,
    debouncedScenarios,
    courseData?.hashLink,
    saving,
  ]);

  const handleAnswerChange = (index, value) => {
    const updatedScenarios = [...scenarios];
    updatedScenarios[index] = {
      ...updatedScenarios[index],
      studentAnswerText: value,
    };
    setScenarios(updatedScenarios);
  };

  const getWordCount = (text) => {
    return text.trim().split(/\s+/).filter(Boolean).length;
  };

  const renderScenario = (scenario, index) => {
    const wordCount = getWordCount(scenario.studentAnswerText);
    const hasMinimumWords = wordCount >= 50;

    return (
      <Segment key={scenario.userScenarioId} raised>
        <Header as="h3">Scenario {index + 1}</Header>
        <p>{scenario.scenarioText}</p>
        <Form>
          <TextArea
            placeholder="Enter your answer here..."
            value={scenario.studentAnswerText}
            onChange={(e) => handleAnswerChange(index, e.target.value)}
            rows={4}
            style={{ resize: 'none' }}
            disabled={courseData?.scenarioInfo?.disabled}
          />
        </Form>
        <div
          style={{
            marginTop: '1em',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5em',
          }}
        >
          {!hasMinimumWords && (
            <Icon
              name="times circle"
              color="red"
              style={{ paddingBottom: '20px' }}
            />
          )}
          {hasMinimumWords && (
            <Icon
              name="check circle"
              color="green"
              style={{ paddingBottom: '20px' }}
            />
          )}
          <span>Word Count: {wordCount}</span>
        </div>

        {scenario.score !== null && (
          <Message positive style={{ marginTop: '1em' }}>
            <Message.Header>Score</Message.Header>
            <p>{scenario.score}/100</p>
          </Message>
        )}

        {scenario.instructorFeedback && (
          <Message info style={{ marginTop: '1em' }}>
            <Message.Header>Instructor Feedback</Message.Header>
            <p>{scenario.instructorFeedback}</p>
          </Message>
        )}
      </Segment>
    );
  };

  return (
    <Container style={{ maxWidth: '800px', padding: '2em 0' }}>
      <SavingIndicator saving={saving} lastSaved={lastSaved} />
      <Header as="h2" textAlign="center" style={{ marginBottom: '1.5em' }}>
        {courseData?.title}
      </Header>
      <Grid>
        {scenarios.map((scenario, index) => (
          <Grid.Row key={scenario.userScenarioId}>
            <Grid.Column>{renderScenario(scenario, index)}</Grid.Column>
          </Grid.Row>
        ))}
      </Grid>
      {scenarios.every((scenario) => scenario.score === null) &&
        !scenarios.every((scenario) => scenario.instructorFeedback) && (
          <div style={{ marginTop: '1em' }}>
            <p>
              * The DMV requires all answers to be at least 50 words and focused
              on the question topic.
            </p>
          </div>
        )}
    </Container>
  );
};

export default CoursePageScenario;
